import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Text,
} from '@chakra-ui/react';
import { AutoEndWarning } from '~/common/api/zeiterfassungServerApi/AutoEndWarningApi';
import { LogoutWarning } from '~/common/api/zeiterfassungServerApi/LogoutWarningApi';
import { PauseWarning } from '~/common/api/zeiterfassungServerApi/PauseWarningApi';
import { DurationViewer } from '~/common/components/defaultUIComponents';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { formatName } from '~/common/utils/employeeUtils';
import useTranslate from '~/common/utils/translate';
import { format } from 'date-fns';

type WarningDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  warning: AutoEndWarning | LogoutWarning | PauseWarning | null;
  employee: Employee;
  date: number;
};

const isAutoEndWarning = (
  warning: AutoEndWarning | LogoutWarning | PauseWarning | null,
): warning is AutoEndWarning => warning?.type === 'autoEnd';

const isLogoutWarning = (
  warning: AutoEndWarning | LogoutWarning | PauseWarning | null,
): warning is LogoutWarning => warning?.type === 'logout';

const isPauseWarning = (
  warning: AutoEndWarning | LogoutWarning | PauseWarning | null,
): warning is PauseWarning => warning?.type === 'pause';

const WarningDrawer: React.FC<WarningDrawerProps> = ({
  isOpen,
  onClose,
  warning,
  employee,
  date,
}) => {
  const t = useTranslate();
  if (!warning) {
    return (
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader></DrawerHeader>
          <DrawerBody></DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay />
      {isAutoEndWarning(warning) && (
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Text>{t('warnings.drawer.autoEnd.header')}</Text>
            {t('warnings.of', [
              formatName(employee),
              format(date, 'dd.MM.yyy'),
            ])}
          </DrawerHeader>

          <DrawerBody>
            {t('warnings.drawer.autoEnd.description.one')}
            <br />
            <br />
            {t('warnings.drawer.autoEnd.description.two')}
            <DurationViewer
              label="warnings.pause.duration"
              durationInSeconds={warning.pause_duration}
            />
          </DrawerBody>
        </DrawerContent>
      )}
      {isLogoutWarning(warning) && (
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Text>{t('warnings.drawer.logout.header')}</Text>
            {t('warnings.of', [
              formatName(employee),
              format(date as unknown as number, 'dd.MM.yyyy'),
            ])}
          </DrawerHeader>
          <DrawerBody>
            <Text>{warning.reason}</Text>
          </DrawerBody>
        </DrawerContent>
      )}
      {isPauseWarning(warning) && (
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Text>{t('warnings.drawer.pause.header')}</Text>
            {t('warnings.of', [
              formatName(employee),
              format(date as unknown as number, 'dd.MM.yyyy'),
            ])}
          </DrawerHeader>
          <DrawerBody>
            <Text>{warning.reason}</Text>
          </DrawerBody>
        </DrawerContent>
      )}
    </Drawer>
  );
};

export default WarningDrawer;
